import {Flex, TextInput, Button, useMantineTheme, Menu, Checkbox, Stack, Group, Badge, ActionIcon} from "@mantine/core";
import {MaterialSymbol} from "react-material-symbols";
import {OrderStatus} from "../../../interfaces/OrderInterfaces";
import checkboxClasses from "../../../styles/CheckboxShared.module.css";

export interface OrderFilterValues {
    marketplaces: string[];
    statuses: string[];
    search: string;
    startDate?: string;
    endDate?: string;
    warnings: string[];
}

interface OrderFiltersProps {
    filters: OrderFilterValues;
    setFilters: (filters: OrderFilterValues) => void;
    onRefresh: () => void;
    refreshing?: boolean;
}

// Traduzioni degli stati in italiano
const statusLabels: Record<string, string> = {
    [OrderStatus.STAGING]: 'In Attesa',
    [OrderStatus.WAITING_ACCEPTANCE]: 'Da Accettare',
    [OrderStatus.WAITING]: 'In Lavorazione',
    [OrderStatus.WAITING_DEBIT]: 'Attesa Addebito',
    [OrderStatus.WAITING_DEBIT_PAYMENT]: 'Attesa Pagamento',
    [OrderStatus.SHIPPING]: 'Da Spedire',
    [OrderStatus.SHIPPED]: 'Spedito',
    [OrderStatus.RECEIVED]: 'Ricevuto',
    [OrderStatus.CLOSED]: 'Chiuso',
    [OrderStatus.REFUSED]: 'Rifiutato',
    [OrderStatus.CANCELED]: 'Annullato',
    [OrderStatus.REFUNDED]: 'Rimborsato',
    [OrderStatus.INCIDENT_OPEN]: 'Incidente',
    [OrderStatus.UNKNOWN]: 'Sconosciuto',
};

const OrderFilters = ({filters, setFilters, onRefresh, refreshing}: OrderFiltersProps) => {
    const theme = useMantineTheme();

    const statusOptions = Object.values(OrderStatus).map(s => ({
        value: s,
        label: statusLabels[s] || s
    }));

    const handleStatusChange = (status: string, checked: boolean) => {
        const newStatuses = checked
            ? [...filters.statuses, status]
            : filters.statuses.filter(s => s !== status);
        setFilters({...filters, statuses: newStatuses});
    };

    const handleWarningsChange = (value: string, checked: boolean) => {
        const newWarnings = checked
            ? [...filters.warnings, value]
            : filters.warnings.filter(w => w !== value);
        setFilters({...filters, warnings: newWarnings});
    };

    const clearFilters = () => {
        setFilters({
            marketplaces: [],
            statuses: [],
            search: '',
            startDate: undefined,
            endDate: undefined,
            warnings: []
        });
    };

    const activeFiltersCount = filters.statuses.length + 
        (filters.startDate ? 1 : 0) + (filters.endDate ? 1 : 0) + filters.warnings.length;

    return (
        <Flex gap="sm" align="flex-end" wrap="wrap" mb="md">
            {/* Ricerca */}
            <TextInput
                placeholder="Cerca ID Ordine, Cliente, SKU..."
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                leftSection={<MaterialSymbol icon="search" size={20}/>}
                radius="md"
                style={{width: 250}}
            />

            {/* Menu Stato */}
            <Menu shadow="md" width={250} closeOnItemClick={false}>
                <Menu.Target>
                    <Button
                        variant="outline"
                        color={theme.colors.textBlack?.[4] || 'gray'}
                        leftSection={<MaterialSymbol icon="check_circle" size={20}/>}
                        rightSection={filters.statuses.length > 0 && <Badge size="sm" circle>{filters.statuses.length}</Badge>}
                        radius="md"
                        style={{minWidth: 160}}
                    >
                        Stato
                    </Button>
                </Menu.Target>
                <Menu.Dropdown>
                    <Stack gap="xs" p="xs">
                        {statusOptions.map(option => (
                            <Checkbox
                                key={option.value}
                                label={option.label}
                                checked={filters.statuses.includes(option.value)}
                                onChange={(e) => handleStatusChange(option.value, e.currentTarget.checked)}
                                size="sm"
                                radius="sm"
                                classNames={{ 
                                    input: checkboxClasses.checkboxInput, 
                                    icon: checkboxClasses.checkboxIcon, 
                                    inner: checkboxClasses.checkboxInner,
                                    label: checkboxClasses.checkboxLabel
                                }}
                            />
                        ))}
                    </Stack>
                </Menu.Dropdown>
            </Menu>

            {/* Menu Data */}
            <Menu shadow="md" width={220} closeOnItemClick={false}>
                <Menu.Target>
                    <Button
                        variant="outline"
                        color={theme.colors.textBlack?.[4] || 'gray'}
                        leftSection={<MaterialSymbol icon="calendar_today" size={20}/>}
                        rightSection={(filters.startDate || filters.endDate) && <Badge size="sm" circle>1</Badge>}
                        radius="md"
                        style={{minWidth: 150}}
                    >
                        Data
                    </Button>
                </Menu.Target>
                <Menu.Dropdown>
                    <Stack gap="xs" p="xs">
                        <TextInput
                            label="Data Inizio"
                            type="date"
                            value={filters.startDate || ''}
                            onChange={(e) => setFilters({...filters, startDate: e.currentTarget.value || undefined})}
                            size="sm"
                        />
                        <TextInput
                            label="Data Fine"
                            type="date"
                            value={filters.endDate || ''}
                            onChange={(e) => setFilters({...filters, endDate: e.currentTarget.value || undefined})}
                            size="sm"
                        />
                    </Stack>
                </Menu.Dropdown>
            </Menu>

            {/* Menu Avvertimenti */}
            <Menu shadow="md" width={250} closeOnItemClick={false}>
                <Menu.Target>
                    <Button
                        variant="outline"
                        color={theme.colors.textBlack?.[4] || 'gray'}
                        leftSection={<MaterialSymbol icon="warning" size={20}/>}
                        rightSection={filters.warnings.length > 0 && <Badge size="sm" circle>{filters.warnings.length}</Badge>}
                        radius="md"
                        style={{minWidth: 180}}
                    >
                        Avvertimenti
                    </Button>
                </Menu.Target>
                <Menu.Dropdown>
                    <Stack gap="xs" p="xs">
                        <Checkbox
                            label="Con quantità insufficiente"
                            checked={filters.warnings.includes('with_warnings')}
                            onChange={(e) => handleWarningsChange('with_warnings', e.currentTarget.checked)}
                            size="sm"
                            radius="sm"
                            classNames={{ 
                                input: checkboxClasses.checkboxInput, 
                                icon: checkboxClasses.checkboxIcon, 
                                inner: checkboxClasses.checkboxInner,
                                label: checkboxClasses.checkboxLabel
                            }}
                        />
                        <Checkbox
                            label="Senza avvertimenti"
                            checked={filters.warnings.includes('without_warnings')}
                            onChange={(e) => handleWarningsChange('without_warnings', e.currentTarget.checked)}
                            size="sm"
                            radius="sm"
                            classNames={{ 
                                input: checkboxClasses.checkboxInput, 
                                icon: checkboxClasses.checkboxIcon, 
                                inner: checkboxClasses.checkboxInner,
                                label: checkboxClasses.checkboxLabel
                            }}
                        />
                    </Stack>
                </Menu.Dropdown>
            </Menu>

            {/* Pulsante Aggiorna */}
            <Button
                onClick={onRefresh}
                variant="outline"
                color={theme.colors.textBlack?.[4] || 'gray'}
                leftSection={<MaterialSymbol icon="refresh" size={20}/>}
                loading={refreshing}
                radius="md"
            >
                Aggiorna
            </Button>

            {/* Pulsante Pulisci Filtri */}
            {activeFiltersCount > 0 && (
                <Button
                    onClick={clearFilters}
                    variant="light"
                    color="red"
                    leftSection={<MaterialSymbol icon="filter_alt_off" size={20}/>}
                    radius="md"
                >
                    Pulisci ({activeFiltersCount})
                </Button>
            )}
        </Flex>
    );
};

export default OrderFilters;
